/**
 * Create Organization Dialog Component
 *
 * Dialog for creating new organizations.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';

export default function CreateOrganizationDialog({ open, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    provision_dns: false,
    subscription_plan: 'free'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (event) => {
    const value = field === 'provision_dns' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.name || !formData.slug || !formData.admin_email || !formData.admin_name || !formData.admin_password) {
        setError('All fields are required');
        return;
      }

      if (formData.admin_password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }

      if (!/^[a-z0-9-_]+$/.test(formData.slug)) {
        setError('Slug must contain only lowercase letters, numbers, hyphens, and underscores');
        return;
      }

      await onSubmit(formData);

      // Reset form
      setFormData({
        name: '',
        slug: '',
        admin_email: '',
        admin_name: '',
        admin_password: '',
        provision_dns: false,
        subscription_plan: 'free'
      });
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        slug: '',
        admin_email: '',
        admin_name: '',
        admin_password: '',
        provision_dns: false,
        subscription_plan: 'free'
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Organization</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Organization Name"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
            helperText="The name of the organization"
          />

          <TextField
            label="Slug (Subdomain)"
            value={formData.slug}
            onChange={handleChange('slug')}
            fullWidth
            required
            helperText="Will be used as subdomain: slug.app.ailyce.ai"
            InputProps={{
              style: { fontFamily: 'monospace' }
            }}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: -1, fontWeight: 600 }}>
            Admin User Details
          </Typography>

          <TextField
            label="Admin Email"
            type="email"
            value={formData.admin_email}
            onChange={handleChange('admin_email')}
            fullWidth
            required
            helperText="Email for the organization admin user"
          />

          <TextField
            label="Admin Full Name"
            value={formData.admin_name}
            onChange={handleChange('admin_name')}
            fullWidth
            required
          />

          <TextField
            label="Admin Password"
            type="password"
            value={formData.admin_password}
            onChange={handleChange('admin_password')}
            fullWidth
            required
            helperText="Minimum 8 characters"
          />

          <FormControl fullWidth>
            <InputLabel>Subscription Plan</InputLabel>
            <Select
              value={formData.subscription_plan}
              onChange={handleChange('subscription_plan')}
              label="Subscription Plan"
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="starter">Starter</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
              <MenuItem value="enterprise">Enterprise</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.provision_dns}
                onChange={handleChange('provision_dns')}
              />
            }
            label="Provision DNS (Cloudflare)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating...' : 'Create Organization'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
